import { Trans, useTranslation } from "react-i18next";
import { Modal, ModalContent, ModalTitle, useMuiModal } from "./modal";
import type { CollectorsDiscountData } from "@/hooks/use-apply-payment";
import { formatDate } from "@/lib/utils";
import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { useShop } from "@/hooks/use-shop";
import { useAudioSlot } from "@/features/audio/kiosk-audio";

interface CollectorsDiscountDialogProps {
  data: CollectorsDiscountData;
}

export function useAutocloseModalAfterMs(ms: number) {
  const { closeModal } = useMuiModal();
  const [remainingMs, setRemainingMs] = useState(ms);

  useEffect(() => {
    const timer = setTimeout(() => {
      closeModal();
    }, ms);

    const interval = setInterval(() => {
      setRemainingMs((prev) => Math.max(0, prev - 100));
    }, 100);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [closeModal, ms]);

  return { remainingMs };
}

export function CollectorsDiscountModal({
  data,
}: CollectorsDiscountDialogProps) {
  useAudioSlot("modal.collectors-discount");
  const hasCoupon = !!data.couponCode;
  const { t } = useTranslation();
  const { closeModal } = useMuiModal();
  const shop = useShop();

  const receivedTransactions = data.receivedTransactions;

  const { remainingMs } = useAutocloseModalAfterMs(20_000);
  const remainingSeconds = Math.ceil(remainingMs / 1000);

  return (
    <Modal>
      <ModalTitle>
        {hasCoupon
          ? data.yHead || t("Gratulálunk!")
          : data.xHead || t("Leárazás")}
      </ModalTitle>
      <ModalContent>
        {!hasCoupon ? (
          <div>
            <div>
              <span>
                {data.xBody || (
                  <Trans>
                    A tranzakcióddal részt vettél egy leárazásban. Indíts több
                    tranzakciót és szerezz kuponokat!
                  </Trans>
                )}
              </span>
            </div>
            <div>
              <span>
                <Trans>
                  Kedvezmény eléréséhez szükséges tranzakciók száma:
                </Trans>
                {data.neededTransactions}
              </span>
            </div>
            <div>
              <span>
                {t("Eddig összegyűjtött tranzakciók száma:")}{" "}
                {data.collectorsDiscountCount}
              </span>
            </div>
          </div>
        ) : (
          <div>
            <div>
              <span>
                {data.yBody ||
                  t(
                    "Sikeresen elérted a szükséges tranzakció számot, mely után az alábbi kedvezményes kupont kapod most tőlünk ajándékba!",
                    data,
                  )}
              </span>
            </div>
            <div>
              <span>
                {t("Kedvezmény mértéke: ")} {data.discount}{" "}
                {data.discountType === "fix"
                  ? shop.data.currencyCode === "EUR"
                    ? "€"
                    : "Ft"
                  : "%"}
              </span>
            </div>
            {(data.machineType?.length ?? 0) > 0 && (
              <div>
                <span>
                  {t("Erre tudod felhasználni:")}{" "}
                  {data
                    .machineType!.map((elem) =>
                      elem === "mosogep"
                        ? t("Mosógép")
                        : elem === "szaritogep"
                          ? t("Szárítógép")
                          : t("Ózon"),
                    )
                    .join(", ")}
                </span>
              </div>
            )}
            <div className="my-2">
              <span className="inline-flex items-center gap-2">
                {t("Kuponkód")}: <b className="text-xl">{data.couponCode}</b>
              </span>
            </div>
            <div>
              <span>
                {t("Felhasználható: ")}{" "}
                {data.couponExpiry ? formatDate(data.couponExpiry) : ""}{" "}
                <Trans>-ig, {{ receivedTransactions }} alkalommal</Trans>
              </span>
            </div>
          </div>
        )}

        <div className="mt-4 text-center text-sm text-muted-foreground">
          <Trans>
            Ez az ablak {{ remainingSeconds }} másodperc múlva bezárul
          </Trans>
        </div>

        <div>
          <Button onClick={() => closeModal(true)}>
            <Trans>Ok</Trans>
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
}
